import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Structures/Navbar';
import Footer from '../components/Structures/Footer';
import {
  FaSearch,
  FaFileAlt,
  FaComments,
  FaCodeBranch,
  FaMicrophone,
  FaGavel,
} from 'react-icons/fa';

import systemInsightsLogo from '../assets/images/SystemInsights.png';

const features = [
  {
    icon: FaSearch,
    title: 'AI Search',
    description:
      'Find relevant legislation instantly using AI-powered semantic search across bills, statutes, and legislative history.',
  },
  {
    icon: FaFileAlt,
    title: 'Bill Summary',
    description:
      'Get concise, AI-generated summaries of complex legislation — understand the impact of any bill in seconds.',
  },
  {
    icon: FaComments,
    title: 'AI Query',
    description:
      'Ask natural language questions about legislation and receive accurate, sourced answers drawn from legislative data.',
  },
  {
    icon: FaCodeBranch,
    title: 'Version Comparison',
    description:
      'Compare bill versions side-by-side with AI-highlighted changes — instantly see what was added, removed, or modified.',
  },
  {
    icon: FaMicrophone,
    title: 'Floor Transcripts',
    description:
      'Access AI-processed floor session transcripts with searchable text, speaker identification, and key moment highlights.',
  },
  {
    icon: FaGavel,
    title: 'Committee Transcripts',
    description:
      'Review committee hearing transcripts with AI analysis — track testimony, questions, and legislative intent.',
  },
];

const platforms = [
  { name: 'Telicon', state: 'Texas', url: 'https://www.telicon.com/' },
  { name: 'Legiscon', state: 'Louisiana', url: 'https://www.legiscon.com/' },
  { name: 'NY StateWatch', state: 'New York', url: 'https://nystatewatch.net/' },
  { name: 'MS StateWatch', state: 'Mississippi', url: 'https://msstatewatch.net/' },
  { name: 'LegisOK', state: 'Oklahoma', url: 'https://legisok.com/' },
  { name: 'AR Digest', state: 'Arkansas', url: 'https://ardigest.net/' },
];

const testimonials = [
  {
    quote:
      'The tools that I use are the BEST! I have used other services and they don\'t compare. I have had such significant positive feedback from clients about the bill comparison tool and the bill brief is pretty incredible too.',
    author: 'John Williams',
    role: 'Principal, Capital Consulting Group',
  },
  {
    quote:
      "I've been using the AI tools for the past year and the entire suite has transformed my workflow. From quickly sifting through committee testimony to find relevant bills, to using the chat feature for instant bill research — every new feature has made a meaningful impact on how I work.",
    author: 'Matt B.',
    role: 'Owner/Lobbyist, Bentley Public Affairs',
  },
  {
    quote:
      'These tools give us enhanced capabilities to keep our clients up-to-speed — in real time — with what\'s happening at the Capitol. From text alerts to the new AI tools that allow us to more efficiently analyze pending legislation, the platform is critical to our operations.',
    author: 'Pete Martinez',
    role: 'Managing Partner, Martinez Strategic Alliances',
  },
];

const Home = () => {
  const navigate = useNavigate();

  const [visibleWords, setVisibleWords] = useState(0);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const [platformsVisible, setPlatformsVisible] = useState(false);
  const [testimonialsVisible, setTestimonialsVisible] = useState(false);
  const featuresRef = useRef(null);
  const platformsRef = useRef(null);
  const testimonialsRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      navigate(`/${user.state}/search/${user.searchTerm || ''}`);
    }
  }, [navigate]);

  // Stagger word animations on mount
  useEffect(() => {
    const headlineWords = 'AI-Powered Legislative Intelligence'.split(' ');
    headlineWords.forEach((_, index) => {
      setTimeout(() => {
        setVisibleWords(index + 1);
      }, 300 + index * 100);
    });
  }, []);

  // Intersection observers for scroll animations
  useEffect(() => {
    const observerOptions = { threshold: 0.2, rootMargin: '0px 0px -80px 0px' };

    const featuresObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setFeaturesVisible(true);
    }, observerOptions);

    const platformsObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setPlatformsVisible(true);
    }, observerOptions);

    const testimonialsObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setTestimonialsVisible(true);
    }, observerOptions);

    if (featuresRef.current) featuresObserver.observe(featuresRef.current);
    if (platformsRef.current) platformsObserver.observe(platformsRef.current);
    if (testimonialsRef.current) testimonialsObserver.observe(testimonialsRef.current);

    return () => {
      featuresObserver.disconnect();
      platformsObserver.disconnect();
      testimonialsObserver.disconnect();
    };
  }, []);

  return (
    <div className="font-inter bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50/50 to-white pt-32 pb-20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 rounded-full bg-lightblue/10 blur-3xl"></div>
          <div className="absolute top-1/3 right-20 w-80 h-80 rounded-full bg-darkblue/5 blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <div className="text-center lg:text-left">
              <p className="text-darkblue text-lg font-semibold tracking-wider uppercase mb-4">
                By System Insights
              </p>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8 text-gray-900">
                {'AI-Powered Legislative Intelligence'.split(' ').map((word, index) => (
                  <span
                    key={index}
                    className={`inline-block transition-all duration-500 ${
                      index < visibleWords
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-2'
                    }`}
                    style={{ marginRight: '0.25em' }}
                  >
                    {word}
                  </span>
                ))}
              </h1>

              <p
                className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-10 max-w-xl transition-all duration-700"
                style={{
                  opacity: visibleWords >= 1 ? 1 : 0,
                  transform: visibleWords >= 1 ? 'translateY(0)' : 'translateY(10px)',
                  transitionDelay: '100ms',
                }}
              >
                Advanced AI tools that extend the platforms you already use — delivering instant
                search, bill summaries, version comparison, and transcript analysis to legislative
                professionals.
              </p>

              <div
                className="flex flex-wrap gap-4 lg:justify-start justify-center transition-all duration-700"
                style={{
                  opacity: visibleWords >= 1 ? 1 : 0,
                  transform: visibleWords >= 1 ? 'translateY(0)' : 'translateY(10px)',
                  transitionDelay: '200ms',
                }}
              >
                <a
                  href="#features"
                  className="bg-darkblue hover:bg-darkblue/90 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transition-all duration-300 hover:scale-105 inline-flex items-center"
                >
                  Explore Tools
                </a>
                <a
                  href="#platforms"
                  className="border-2 border-darkblue text-darkblue hover:bg-darkblue hover:text-white px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-300 inline-flex items-center"
                >
                  Our Platforms
                </a>
              </div>
            </div>

            {/* Right side — logo showcase */}
            <div className="relative hidden lg:flex flex-col items-center justify-center">
              <div
                className="absolute inset-0 -inset-x-16 -inset-y-8 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle at 50% 40%, rgba(24, 33, 88, 0.06) 0%, rgba(255, 255, 255, 0) 70%)',
                }}
              ></div>
              <div className="relative z-10 bg-white/80 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-gray-200/50">
                <img
                  src={systemInsightsLogo}
                  alt="System Insights - Legislative Information Systems"
                  className="w-full max-w-md"
                />
                <p className="text-center text-gray-500 text-sm mt-4 font-medium">
                  Extending the power of legislative tracking with AI
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="h-16 bg-gradient-to-t from-white to-transparent absolute bottom-0 left-0 right-0"></div>
      </section>

      {/* Features Section */}
      <section id="features" ref={featuresRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div
            className="text-center mb-16 transition-all duration-1000"
            style={{
              opacity: featuresVisible ? 1 : 0,
              transform: featuresVisible ? 'translateY(0)' : 'translateY(30px)',
            }}
          >
            <span className="text-darkblue text-lg font-bold tracking-wider uppercase">AI Tools</span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900 mt-2">
              What We Offer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI suite enhances your existing legislative platform with powerful tools
              built for the way policy professionals actually work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl p-8 transition-all duration-500 hover:shadow-lg hover:-translate-y-1"
                  style={{
                    opacity: featuresVisible ? 1 : 0,
                    transform: featuresVisible ? 'translateY(0)' : 'translateY(30px)',
                    transitionDelay: `${index * 100}ms`,
                  }}
                >
                  <div className="bg-darkblue/10 rounded-full p-4 w-fit mb-5">
                    <Icon className="text-darkblue" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600 leading-7">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section id="platforms" ref={platformsRef} className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4">
          <div
            className="text-center mb-16 transition-all duration-1000"
            style={{
              opacity: platformsVisible ? 1 : 0,
              transform: platformsVisible ? 'translateY(0)' : 'translateY(30px)',
            }}
          >
            <span className="text-darkblue text-lg font-bold tracking-wider uppercase">
              Integrated Platforms
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900 mt-2">
              Built to Extend What You Already Use
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI tools are available as an extension of these trusted legislative tracking
              platforms. Access them directly from the platform you already work in.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {platforms.map((platform, index) => (
              <a
                key={index}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-gray-200 rounded-xl p-6 transition-all duration-500 hover:shadow-lg hover:-translate-y-1 hover:border-darkblue/30 group"
                style={{
                  opacity: platformsVisible ? 1 : 0,
                  transform: platformsVisible ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: `${index * 80}ms`,
                }}
              >
                <h3 className="text-xl font-semibold text-gray-800 group-hover:text-darkblue transition-colors mb-1">
                  {platform.name}
                </h3>
                <p className="text-gray-500 text-sm">{platform.state}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div
            className="text-center mb-16 transition-all duration-1000"
            style={{
              opacity: testimonialsVisible ? 1 : 0,
              transform: testimonialsVisible ? 'translateY(0)' : 'translateY(30px)',
            }}
          >
            <span className="text-darkblue text-lg font-bold tracking-wider uppercase">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900 mt-2">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg border border-gray-200 transition-all duration-500"
                style={{
                  opacity: testimonialsVisible ? 1 : 0,
                  transform: testimonialsVisible ? 'translateY(0)' : 'translateY(30px)',
                  transitionDelay: `${index * 150}ms`,
                }}
              >
                <blockquote className="text-gray-600 italic mb-6 leading-7">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                  <div className="w-12 h-12 rounded-full bg-darkblue flex items-center justify-center text-white font-semibold text-sm">
                    {testimonial.author
                      .split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{testimonial.author}</p>
                    <p className="text-xs text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(131,178,250,0.1),transparent_70%)]"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight text-white">
              Smarter Legislative Intelligence Starts Here
            </h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto">
              Access AI-powered search, summaries, and analysis directly from the legislative
              platform you already use.
            </p>
            <a
              href="#platforms"
              className="inline-flex items-center bg-white text-gray-900 hover:bg-gray-100 px-10 py-5 text-xl font-bold rounded-xl shadow-2xl transition-all duration-300 hover:scale-105 group"
            >
              View Our Platforms
              <span className="inline-block ml-2 transition-transform group-hover:translate-x-1">
                &rarr;
              </span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
